import React, { useState, useMemo, useEffect } from 'react';
import Pagination from '@components/Layout/Pagination';
import UserTable from '@components/Layout/UserTable';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'react-bootstrap';
import { BiSort } from 'react-icons/bi';
import NotificationTable from '@components/Layout/NotificationTable';
import { CREATE_NOTIFICATION_PATH } from '@constants/routes';
import { useNavigate } from 'react-router-dom';
import ActivityLogTable from '@components/Layout/ActivityLogTable';
import { getActivityLogList } from '@utils/sharedUtils';
import { FaFilter } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useAppSelector } from '@store/hooks';
import SearchBar from '@components/Layout/SearchBar';
import DataLoader from '@components/DataLoader';

const MySwal = Swal;

const parseDate = (dateString: string): Date => {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
};

type CustomDropdownToggleProps = {
    children?: React.ReactNode;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const CustomDropdownToggle = React.forwardRef<
    HTMLButtonElement,
    CustomDropdownToggleProps
>(({ children, onClick }, ref) => (
    <button
        ref={ref}
        onClick={(e) => {
            e.preventDefault();
            onClick?.(e);
        }}
        className="btn !bg-primary py-2 !text-white"
    >
        {children}
        <FaFilter className="ml-2" /> {/* Add your custom icon here */}
    </button>
));

const ActivityLogList = () => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('newest');
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [filterField, setFilterField] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [activityLogList, setActivityLogList] = useState<any[]>([]);
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState<any>(null);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const loggedInUserRole = useAppSelector(
        (state) => state.login.loggedInUserRole
    );
    const loggedInUserTenantID = useAppSelector(
        (state) => state.login.loggedInUserTenantID
    );

    const fetchData = async (loadMore = false) => {
        try {
            const { logLists, lastEvaluatedKey: newKey } = await getActivityLogList(
                loggedInUserRole,
                loggedInUserTenantID,
                10,
                loadMore ? lastEvaluatedKey : undefined
            );
            if (logLists && logLists?.length > 0) {
                setActivityLogList(prevLogs => loadMore ? [...prevLogs, ...logLists] : logLists);
            }
            setLastEvaluatedKey(newKey);
            setHasMore(!!newKey);
        } catch (error: any) {
            let errorMessage = 'Unexpected error';

            switch (error.name) {
                case 'ResourceNotFoundException':
                    errorMessage = 'Table or item not found.';
                    break;
                case 'TooManyRequestsException':
                    errorMessage = 'Too many requests. Please try again later.';
                    break;
                case 'ProvisionedThroughputExceededException':
                    errorMessage =
                        'Request limit exceeded. Please try again later.';
                    break;
                case 'ServiceException':
                    errorMessage = `Service error: ${error.message}`;
                    break;
                default:
                    errorMessage = `Unexpected error: ${error.message}`;
                    break;
            }
            MySwal.fire({
                position: 'top-end', // Position the toast in the top-right corner
                icon: 'error',
                title: errorMessage,
                showConfirmButton: false,
                timer: 3000,
                toast: true,
                customClass: {
                    popup: '!text-red !bg-light_red', // Add custom class to the popup
                },
                width: 'auto',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const [tableHeading, setTableHeading] = useState([
        {
            id: 1,
            title: 'Date',
        },
        {
            id: 2,
            title: 'Time',
        },
        {
            id: 3,
            title: 'Device ID',
        },
        {
            id: 4,
            title: 'User',
        },
        {
            id: 5,
            title: 'Action',
        },
        // Add more user objects here
    ]);
    const handelCreateNotification = () => {
        navigate(CREATE_NOTIFICATION_PATH);
    };
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleSortChange = (event: string) => {
        setSortField(event);
    };

    const filteredUsers = useMemo(() => {
        let filtered = activityLogList;

        if (searchTerm) {
            filtered = filtered.filter(
                (user) =>
                    user.date
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    user.device_id
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    user.name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return filtered;
    }, [searchTerm, filterField, statusFilter, activityLogList]);

    const sortedData = useMemo(() => {
        let sorted = [...filteredUsers];

        if (sortField !== undefined && sortField !== null) {
            sorted.sort((a: any, b: any) => {
                const dateA = new Date(a.created_at);
                const dateB = new Date(b.created_at);

                if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                    if (sortField.toLowerCase() === 'newest') {
                        return dateB.getTime() - dateA.getTime();
                    } else if (sortField.toLowerCase() === 'oldest') {
                        return dateA.getTime() - dateB.getTime();
                    }
                }

                // Handle cases where one or both dates are invalid
                if (isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                    return sortField.toLowerCase() === 'newest' ? 1 : -1; // Treat invalid dateA as older/newer
                }
                if (!isNaN(dateA.getTime()) && isNaN(dateB.getTime())) {
                    return sortField.toLowerCase() === 'newest' ? -1 : 1; // Treat invalid dateB as older/newer
                }

                return 0;
            });
        }

        return sorted;
    }, [filteredUsers, sortField]);

    return (
        <>
            <div className="">
                <div className="mb-5">
                    <h1 className="text-lg font-semibold">
                        {t('activityLog.title')}
                    </h1>
                </div>
                <div
                    className="flex justify-content-between
                 align-items-center my-4"
                >
                    <SearchBar
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                    />
                    <Dropdown>
                        <Dropdown.Toggle
                            as={CustomDropdownToggle}
                            variant="primary"
                            id="dropdown-basic"
                        >
                            {t('user_management.individual_user.filter')}
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            <Dropdown.Header>
                                {t('user_management.individual_user.order')}
                            </Dropdown.Header>
                            <Dropdown.Item
                                className="flex items-center"
                                onClick={() => handleSortChange('newest')}
                            >
                                <input
                                    type="radio"
                                    name="newest"
                                    value="newest"
                                    aria-label="newest"
                                    checked={
                                        sortField.toLowerCase() === 'newest'
                                    }
                                    className="mr-2"
                                    readOnly
                                />
                                {t('user_management.individual_user.newest')}
                            </Dropdown.Item>
                            <Dropdown.Item
                                className="flex items-center"
                                onClick={() => handleSortChange('oldest')}
                            >
                                <input
                                    type="radio"
                                    name="oldest"
                                    value="oldest"
                                    aria-label="oldest"
                                    checked={
                                        sortField.toLowerCase() === 'oldest'
                                    }
                                    className="mr-2"
                                    readOnly
                                />
                                {t('user_management.individual_user.oldest')}
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>

                <ActivityLogTable data={sortedData} header={tableHeading} />
                {hasMore && (
                    <div className="text-center mt-4">
                        <button onClick={() => fetchData(true)} className="btn btn-primary">
                            {t('load_more')}
                        </button>
                    </div>
                )}
            </div>
            {loading && <DataLoader />}
        </>
    );
};

export default ActivityLogList;
